/**
 * Phase 261d — Super Admin Adoption Cockpit: PWA install funnel tile.
 *
 * Pulls structured funnel metrics from `/api/super-admin/adoption/pwa-funnel`
 * (which queries PostHog server-side) and renders three sub-panels:
 *
 *   1. Headline KPI strip — unique installers, banner-to-install %, dismiss-to-
 *      reactivation %, total banner views in the window.
 *   2. Per-persona table — desktop vs mobile breakdown with conversion rates.
 *   3. Per-source breakdown — banner vs profile_card vs native.
 *
 * Honest empty states (NEVER fake numbers):
 *   • status='not_configured' → show env vars to set, link to PostHog docs
 *   • status='error'          → show raw error + retry button
 *   • shown=0                 → "no PWA events captured yet in this window"
 */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Smartphone, Monitor, Download, RefreshCw, ExternalLink, AlertCircle, BarChart3, Sparkles } from 'lucide-react';

const SOURCE_LABELS = {
  banner: { label: 'Banner', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  profile_card: { label: 'Profile card', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  native: { label: 'Native (URL bar)', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  unknown: { label: 'Other', color: 'bg-slate-50 text-slate-600 border-slate-200' },
};

function fmt(n) {
  if (n == null) return '—';
  if (typeof n !== 'number') return String(n);
  return n.toLocaleString();
}

export default function PwaFunnelTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [windowDays, setWindowDays] = useState(30);

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const r = await api.get(`/super-admin/adoption/pwa-funnel?window_days=${windowDays}${refresh ? '&refresh=1' : ''}`);
      setData(r.data);
    } catch (e) {
      setData({ status: 'error', error: 'fetch_failed', detail: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [windowDays]);

  useEffect(() => { load(false); }, [load]);

  if (loading) {
    return (
      <Card data-testid="pwa-funnel-tile-loading">
        <CardContent className="p-5 space-y-3">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  const isNotConfigured = data?.status === 'not_configured';
  const isError = data?.status === 'error';
  const totals = data?.totals || {};
  const byPersona = data?.by_persona || [];
  const bySource = data?.by_source || [];
  const noEventsYet = !isNotConfigured && !isError && (totals.banner_shown ?? 0) === 0 && (totals.app_installed_native ?? 0) === 0;

  return (
    <Card className="border-l-4 border-l-emerald-500" data-testid="pwa-funnel-tile">
      <CardContent className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-emerald-600" />
              PWA Install Funnel
              {data?.cached && (
                <Badge variant="outline" className="text-[10px]" data-testid="pwa-funnel-cached-badge">cached</Badge>
              )}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              How many academies and students actually install Out-Play after seeing the banner. Source: PostHog.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(parseInt(v, 10))}>
              <SelectTrigger className="w-28 h-8 text-xs" data-testid="pwa-funnel-window-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => load(true)}
              disabled={refreshing}
              data-testid="pwa-funnel-refresh-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* ── State 1: Not configured ────────────────────────────────── */}
        {isNotConfigured && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs space-y-2" data-testid="pwa-funnel-not-configured">
            <p className="font-semibold text-amber-900 dark:text-amber-100 flex items-center gap-1.5">
              <AlertCircle className="h-3.5 w-3.5" /> PostHog backend credentials not configured
            </p>
            <p className="text-amber-800 dark:text-amber-200">
              Set the following env vars on the backend pod, then redeploy:
            </p>
            <ul className="text-amber-900 dark:text-amber-100 list-disc list-inside font-mono text-[11px] space-y-0.5">
              {(data?.missing_env_vars || []).map((v) => <li key={v}>{v}</li>)}
            </ul>
            <p className="text-amber-800 dark:text-amber-200 pt-1">
              <strong>Where to get them:</strong> {data?.instructions?.where_to_get_keys}
            </p>
            <p className="text-amber-800 dark:text-amber-200">
              <strong>Project ID:</strong> {data?.instructions?.where_to_get_project_id}
            </p>
            <a
              href="https://posthog.com/docs/api"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-amber-900 dark:text-amber-100 hover:underline"
              data-testid="pwa-funnel-docs-link"
            >
              PostHog API docs <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}

        {/* ── State 2: Error ──────────────────────────────────────────── */}
        {isError && (
          <div className="rounded-lg border border-rose-300 bg-rose-50 dark:bg-rose-900/20 p-3 text-xs" data-testid="pwa-funnel-error">
            <p className="font-semibold text-rose-900 dark:text-rose-100 flex items-center gap-1.5">
              <AlertCircle className="h-3.5 w-3.5" /> Could not load PostHog funnel
            </p>
            <p className="text-rose-800 dark:text-rose-200 mt-1">
              {data?.error}: {data?.detail || 'Unknown error.'}
            </p>
          </div>
        )}

        {/* ── State 3: Configured but no events yet ───────────────────── */}
        {noEventsYet && (
          <div className="rounded-lg border border-slate-300 bg-slate-50 dark:bg-slate-900/40 p-3 text-xs" data-testid="pwa-funnel-empty">
            <p className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> No PWA events captured yet in the last {data?.window_days} days
            </p>
            <p className="text-slate-700 dark:text-slate-300 mt-1">
              Once users start seeing the install banner or visiting their Profile, events will start flowing in. Check back in a day.
            </p>
          </div>
        )}

        {/* ── State 4: Real data ──────────────────────────────────────── */}
        {!isNotConfigured && !isError && !noEventsYet && (
          <div className="space-y-4" data-testid="pwa-funnel-data">
            {/* KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KPITile label="Unique installers" value={fmt(totals.unique_installers)} icon={Download} accent="emerald" />
              <KPITile label="Banner → install" value={`${totals.overall_install_pct ?? 0}%`} icon={BarChart3} accent="blue" sub={`of ${fmt(totals.banner_shown)} shown`} />
              <KPITile label="Native installs" value={fmt(totals.app_installed_native)} icon={Monitor} accent="slate" sub="via URL-bar / OS" />
              <KPITile label="Second-chance reopens" value={fmt(totals.cooldown_cleared)} icon={RefreshCw} accent="amber" sub={`of ${fmt(totals.banner_dismissed)} dismissed`} />
            </div>

            {/* Per-persona breakdown */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">By persona</h4>
              <div className="overflow-x-auto rounded-lg border" data-testid="pwa-funnel-persona-table">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-900/40 text-slate-600 dark:text-slate-300">
                    <tr>
                      <th className="text-left px-3 py-2">Persona</th>
                      <th className="text-right px-3 py-2">Shown</th>
                      <th className="text-right px-3 py-2">Installed</th>
                      <th className="text-right px-3 py-2">Conv.</th>
                      <th className="text-right px-3 py-2">Dismissed</th>
                      <th className="text-right px-3 py-2">Reopened</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byPersona.map((row) => (
                      <tr key={row.persona} className="border-t" data-testid={`pwa-funnel-row-${row.persona}`}>
                        <td className="px-3 py-2 font-medium capitalize flex items-center gap-1.5">
                          {row.persona === 'desktop' ? <Monitor className="h-3.5 w-3.5 text-slate-500" /> : <Smartphone className="h-3.5 w-3.5 text-slate-500" />}
                          {row.persona}
                        </td>
                        <td className="px-3 py-2 text-right">{fmt(row.banner_shown)}</td>
                        <td className="px-3 py-2 text-right font-medium">{fmt(row.install_accepted)}</td>
                        <td className="px-3 py-2 text-right">
                          <Badge variant="outline" className="text-[10px]">{row.banner_to_install_pct ?? 0}%</Badge>
                        </td>
                        <td className="px-3 py-2 text-right">{fmt(row.banner_dismissed)}</td>
                        <td className="px-3 py-2 text-right">
                          <Badge variant="outline" className="text-[10px]">{row.cooldown_cleared ?? 0} ({row.dismiss_to_reactivate_pct ?? 0}%)</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Per-source breakdown */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Where installs came from</h4>
              <div className="flex flex-wrap gap-2" data-testid="pwa-funnel-source-bars">
                {bySource.length === 0 && (
                  <p className="text-xs text-muted-foreground italic">No installs in this window yet.</p>
                )}
                {bySource.map((s) => {
                  const meta = SOURCE_LABELS[s.source] || SOURCE_LABELS.unknown;
                  return (
                    <Badge key={s.source} variant="outline" className={`text-[11px] ${meta.color}`} data-testid={`pwa-funnel-source-${s.source}`}>
                      {meta.label}: <strong className="ml-1">{fmt(s.installs)}</strong>
                    </Badge>
                  );
                })}
              </div>
            </div>

            <p className="text-[10px] text-muted-foreground italic">
              Window: last {data.window_days} days · Computed: {data.computed_at ? new Date(data.computed_at).toLocaleString() : '—'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function KPITile({ label, value, icon: Icon, accent = 'slate', sub }) {
  const accentMap = {
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    blue: 'text-blue-700 bg-blue-50 border-blue-200',
    slate: 'text-slate-700 bg-slate-50 border-slate-200',
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
  };
  return (
    <div className={`rounded-lg border p-2.5 ${accentMap[accent] || accentMap.slate}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wide font-semibold opacity-80">{label}</span>
        <Icon className="h-3.5 w-3.5 opacity-60" />
      </div>
      <p className="mt-1 text-lg font-bold leading-tight">{value}</p>
      {sub && <p className="text-[10px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
}
